import { bytesToBase64 } from '../crypto-module/safe-base64-std.js';
import { base64ToBytes } from '../crypto-module/safe-base64-std.js';

/**
 * Hybrid-verschlüsseltes Payload-Triple, das der Producer (Frontend) baut
 * und das der Consumer (Praxis-Backend / Mock-Service) entschlüsseln muss.
 *
 * Wire-Format (für das `payload`-Feld der Queue):
 *   JSON.stringify({ iv, encryptedKey, ciphertext })  →  UTF-8  →  Base64
 *
 * Die Queue selbst sieht nur den fertigen Base64-String. Die Verpackung ist
 * eine reine Producer/Consumer-Konvention; die Queue-Spec definiert nur
 * „opaker Ciphertext, Base64-codiert" (siehe
 * `tickets/terminabsage-queue.yaml`).
 *
 * JSON wurde gewählt, weil:
 *   - Debug-Tooling die einzelnen Felder direkt sieht (nach Base64-decode).
 *   - Forward-Compat: neue Felder lassen sich additiv ergänzen.
 *   - Konsumenten implementieren keinen eigenen Längen-Frame-Parser.
 */
export interface QueuePayloadFraming {
  /** AES-GCM-IV (Base64, 16 Zeichen = 12 Byte). */
  readonly iv: string;
  /** RSA-OAEP-gewrappter AES-Key (Base64, exakt 512 Zeichen = 384 Byte). */
  readonly encryptedKey: string;
  /** AES-GCM-Ciphertext inkl. 16-Byte-Auth-Tag (Base64). */
  readonly ciphertext: string;
}

const REQUIRED_KEYS: readonly (keyof QueuePayloadFraming)[] = ['iv', 'encryptedKey', 'ciphertext'];

/**
 * Packt das hybrid-verschlüsselte Triple in den Wire-Payload-String der
 * Queue. Die Felder müssen bereits Base64 sein — diese Funktion macht KEINE
 * Krypto, nur Verpackung.
 */
export function packQueuePayload(framing: QueuePayloadFraming): string {
  const json = JSON.stringify(framing);
  const bytes = new TextEncoder().encode(json);
  return bytesToBase64(bytes);
}

/**
 * Inverse Operation für den Consumer: entpackt den Wire-Payload zurück in
 * das Triple. Wirft bei kaputtem Format einen `Error` mit präzisem Grund.
 */
export function unpackQueuePayload(payload: string): QueuePayloadFraming {
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(payload);
  } catch (cause) {
    throw new Error(`unpackQueuePayload: base64 decode failed: ${describe(cause)}`);
  }
  let json: string;
  try {
    json = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch (cause) {
    throw new Error(`unpackQueuePayload: utf-8 decode failed: ${describe(cause)}`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (cause) {
    throw new Error(`unpackQueuePayload: json parse failed: ${describe(cause)}`);
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('unpackQueuePayload: payload is not an object');
  }
  const record = parsed as Record<string, unknown>;
  for (const key of REQUIRED_KEYS) {
    if (typeof record[key] !== 'string' || (record[key] as string).length === 0) {
      throw new Error(`unpackQueuePayload: missing/empty field "${key}"`);
    }
  }
  return {
    iv: record.iv as string,
    encryptedKey: record.encryptedKey as string,
    ciphertext: record.ciphertext as string,
  };
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
